import { v7 as uuidv7 } from "uuid";

import type {
  V2ContentNode,
  V2Group,
  V2InheritedDef,
  V2Item,
  V2LogbookDef,
} from "../types";
import { isV2Group, isV2Item } from "../types";
import { mergeInherited } from "./merge";

/**
 * Assign runtime IDs, semantic paths, and recursively bake inherited styles into all items in the content tree.
 * @param content - Content nodes to process
 * @param parentInherited - Parent's inherited styles mapping
 * @returns Object with enriched content array
 */
export function assignIds(
  content: V2ContentNode[],
  parentInherited: V2InheritedDef = {},
): {
  content: V2ContentNode[];
} {
  // function createRuntimeId(explicitId: string | undefined): string {
  //   if (explicitId && explicitId !== "auto") {
  //     return explicitId;
  //   }
  //   return uuidv7();
  // }

  function buildSemanticPath(nodeName: string, parentPath: string): string {
    if (parentPath === "root") {
      return nodeName;
    }
    return `${parentPath}.${nodeName}`;
  }

  function walkAndAssign(
    nodes: V2ContentNode[],
    inherited: V2InheritedDef,
    parentPath: string,
  ): V2ContentNode[] {
    return nodes.map(node => {
      if (isV2Group(node)) {
        const nodeName = node.group;
        const nodePath = buildSemanticPath(nodeName, parentPath);
        const merged = mergeInherited(inherited, node.inherited);

        const enrichedGroup = {
          ...node,
          id: node.id ?? uuidv7(),
          _path: nodePath,
          _inherited: merged,
        } as V2Group & { _path: string; _inherited: V2InheritedDef };

        if (enrichedGroup.content) {
          enrichedGroup.content = walkAndAssign(
            enrichedGroup.content,
            merged,
            nodePath,
          );
        }

        return enrichedGroup;
      } else if (isV2Item(node)) {
        const nodeName = node.item;
        const nodePath = buildSemanticPath(nodeName, parentPath);

        const enrichedItem = {
          ...node,
          id: node.id ?? uuidv7(),
          _path: nodePath,
          _inherited: inherited,
        } as V2Item & { _path: string; _inherited: V2InheritedDef };

        return enrichedItem;
      }

      return node;
    });
  }

  const logbook = walkAndAssign(content, parentInherited, "root");

  return { content: logbook };
}

/**
 * Prepare logbook for rendering by assigning UUIDs, paths, and pre-computing inherited styles.
 * @param logbook - Logbook definition to enrich
 * @returns Logbook with generated item IDs and paths
 */
export function prepareLogbookForm(logbook: V2LogbookDef): V2LogbookDef {
  const { content } = assignIds(logbook.content);
  return { ...logbook, content };
}
